import React, { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "../../components/ui/button";
import RazasForm from "../../components/modals/RazasForm";
import { toast } from "react-toastify";
import { Plus, Search, Pencil } from "lucide-react";

const ITEMS_PER_PAGE = 8;

const RazasPage = () => {
  const [razas, setRazas] = useState([]);
  const [filteredRazas, setFilteredRazas] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [selectedRazas, setSelectedRazas] = useState(null);
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  const token = localStorage.getItem("token");
  const API_URL = process.env.REACT_APP_API_URL + "/util";

  const fetchRazas = async () => {
    try {
      const res = await axios.get(`${API_URL}/razas`, { headers: { Authorization: `Bearer ${token}` } });
      setRazas(res.data);
      setFilteredRazas(res.data);
    } catch (err) { console.error(err); }
  };

  useEffect(() => { fetchRazas(); }, []); // eslint-disable-line

  useEffect(() => {
    const term = search.toLowerCase();
    setFilteredRazas(razas.filter((tr) =>
      tr.especie.toLowerCase().includes(term) || tr.raza.toLowerCase().includes(term)
    ));
    setCurrentPage(1);
  }, [search, razas]);

  const totalPages = Math.ceil(filteredRazas.length / ITEMS_PER_PAGE);
  const currentItems = filteredRazas.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);

  const handleSave = async (formData) => {
    try {
      if (selectedRazas) {
        await axios.put(`${API_URL}/up_raza/${selectedRazas.codigo_raza}`, formData, { headers: { Authorization: `Bearer ${token}` } });
        toast.success("Raza actualizada correctamente");
      } else {
        await axios.post(`${API_URL}/new_raza`, formData, { headers: { Authorization: `Bearer ${token}` } });
        toast.success("Raza creada correctamente");
      }
      setShowModal(false);
      fetchRazas();
    } catch (err) {
      toast.error(err.response?.data?.message || err.response?.data?.error || err.message || "Error inesperado");
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
        <div className="relative w-full sm:w-64">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-brand-400" />
          <input type="text" placeholder="Buscar raza..." value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-4 border border-brand-200 rounded-xl py-2 text-sm bg-brand-50 focus:outline-none focus:border-brand-400 focus:ring-2 focus:ring-brand-100" />
        </div>
        <Button onClick={() => { setSelectedRazas(null); setShowModal(true); }} className="gap-2">
          <Plus className="h-4 w-4" /> Nueva raza
        </Button>
      </div>

      <div className="overflow-x-auto rounded-xl border border-brand-100">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="bg-brand-gradient text-white">
              {["Especie COD", "Especie", "Raza COD", "Raza", ""].map((h, i) => (
                <th key={i} className={`px-4 py-3 text-xs font-semibold uppercase tracking-wider whitespace-nowrap ${i === 4 ? "text-right" : "text-left"}`}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-brand-50">
            {currentItems.length > 0 ? currentItems.map((c, i) => (
              <tr key={c._id || c.codigo_raza} className={`transition-colors ${i % 2 === 0 ? "bg-white" : "bg-brand-50/30"} hover:bg-brand-50`}>
                <td className="px-4 py-3"><span className="font-mono text-xs bg-brand-100 text-brand-700 px-2 py-0.5 rounded">{c.codigo_especie}</span></td>
                <td className="px-4 py-3 text-gray-700">{c.especie}</td>
                <td className="px-4 py-3"><span className="font-mono text-xs bg-brand-100 text-brand-700 px-2 py-0.5 rounded">{c.codigo_raza}</span></td>
                <td className="px-4 py-3 font-medium text-gray-800">{c.raza}</td>
                <td className="px-4 py-3 text-right">
                  <Button variant="outline" size="sm" onClick={() => { setSelectedRazas(c); setShowModal(true); }} className="gap-1">
                    <Pencil className="h-3 w-3" /> Editar
                  </Button>
                </td>
              </tr>
            )) : (
              <tr><td colSpan="5" className="py-8 text-center text-gray-400 italic text-sm">No se encontraron razas.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="flex justify-between items-center text-sm text-gray-600">
          <span>{currentItems.length} de {filteredRazas.length}</span>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={currentPage === 1} onClick={() => setCurrentPage((p) => p - 1)}>← Anterior</Button>
            <span className="px-3 py-1.5 bg-brand-50 border border-brand-200 rounded-lg text-brand-700 font-medium">{currentPage}/{totalPages}</span>
            <Button variant="outline" size="sm" disabled={currentPage === totalPages} onClick={() => setCurrentPage((p) => p + 1)}>Siguiente →</Button>
          </div>
        </div>
      )}

      {showModal && <RazasForm especie={selectedRazas} onClose={() => setShowModal(false)} onSave={handleSave} />}
    </div>
  );
};

export default RazasPage;
